// SKADE-native chat (TOC-kontakt) over hubbens collab-kanal — uavhengig av
// Matrix. Ruting følger avsenderens org-scope (som øvrig collab). En Matrix-bro
// kan speile disse begge veier (gna-bridge) uten å endre kontrakten.
//
// Paritet med GNÅ-meldinger: svar, rediger, slett, reaksjoner, skrive-indikator
// og meldingstyper (tekst/tale/bilde/fil). Modellert som en STRØM av hendelser
// (ChatEvent) som reduseres klient-side (event-sourcing, som ESO/agenda i GNÅ).
import { z } from 'zod';
export const ChatKind = z.enum(['text', 'voice', 'image', 'file']);
// Vedlegg-URL: kun trygge skjemaer (hub-media, https, data) — aldri javascript:.
const AttachmentUrl = z.string().max(2048).refine((u) => /^\/media\/[\w.-]+$/.test(u) || u.startsWith('https:') || u.startsWith('data:'), 'ugyldig vedlegg-URL');
/** Én chat-melding (post). Beholdt bakoverkompatibelt (id/channel/author/tsMs/text). */
export const ChatMessageSchema = z.object({
    id: z.string().max(128),
    channel: z.string().max(64).default('net'),
    author: z.string().max(128),
    tsMs: z.number(),
    text: z.string().max(4000).default(''),
    /** Svar på en annen melding (tråd). */
    replyTo: z.string().max(128).optional(),
    kind: ChatKind.default('text'),
    /** Vedlegg for tale/bilde/fil (hub-media-ref eller https/data). */
    attachmentUrl: AttachmentUrl.optional(),
    /** Talelengde (sek) for kind=voice. */
    durationS: z.number().min(0).max(3600).optional(),
});
/** En melding må ha innhold: tekst (kind=text) eller vedlegg (voice/image/file).
 * Hindrer kringkasting av blanke meldinger. */
function hasChatContent(m) {
    return m.kind === 'text' ? m.text.trim().length > 0 : !!m.attachmentUrl;
}
export function parseChatMessage(raw) {
    const r = ChatMessageSchema.safeParse(raw);
    if (!r.success || !hasChatContent(r.data))
        return null;
    return r.data;
}
// ── Hendelses-modell (post/edit/delete/react/typing) ────────────────────────
export const ChatEventSchema = z.discriminatedUnion('op', [
    ChatMessageSchema.extend({ op: z.literal('post') }),
    z.object({ op: z.literal('edit'), id: z.string().max(128), channel: z.string().max(64).default('net'), author: z.string().max(128), tsMs: z.number(), text: z.string().max(4000) }),
    z.object({ op: z.literal('delete'), id: z.string().max(128), channel: z.string().max(64).default('net'), author: z.string().max(128), tsMs: z.number() }),
    z.object({ op: z.literal('react'), id: z.string().max(128), channel: z.string().max(64).default('net'), author: z.string().max(128), tsMs: z.number(), emoji: z.string().max(16), add: z.boolean().default(true) }),
    z.object({ op: z.literal('typing'), channel: z.string().max(64).default('net'), author: z.string().max(128), tsMs: z.number() }),
    // Kanalmedlemskap: legg til / fjern en person (subject) i en kanal. Actor = author
    // (hubben stempler). Persisteres org-scopet (ChatBoard) og replayes så roster
    // overlever reload. `name` = visningsnavn på tidspunktet (praktisk cache).
    z.object({
        op: z.literal('member'), channel: z.string().max(64).default('net'), author: z.string().max(128), tsMs: z.number(),
        subject: z.string().max(128), action: z.enum(['add', 'remove']).default('add'), name: z.string().max(120).optional(),
    }),
    // Kanaltema/-formål (rom-info). Latest tsMs vinner.
    z.object({ op: z.literal('topic'), channel: z.string().max(64).default('net'), author: z.string().max(128), tsMs: z.number(), text: z.string().max(400) }),
]);
export function parseChatEvent(raw) {
    const r = ChatEventSchema.safeParse(raw);
    if (!r.success)
        return null;
    // En 'post' må ha innhold (tekst eller vedlegg) — som parseChatMessage.
    if (r.data.op === 'post' && !hasChatContent(r.data))
        return null;
    return r.data;
}
/** Stemple actor = autentisert avsender (integritet: kan ikke forfalske andre). */
export function stampChatAuthor(e, author) {
    return { ...e, author };
}
/** Reduser en tidsordnet strøm av chat-hendelser til gjeldende meldingsliste.
 * Rediger = nyeste tsMs vinner; slett = gravstein (beholdes som «slettet»);
 * reaksjon = toggler forfatter per emoji. Meldinger sortert på tsMs. */
export function reduceChat(events) {
    const byId = new Map();
    const editedAt = new Map();
    const typing = new Map();
    for (const e of events) {
        if (e.op === 'post') {
            const cur = byId.get(e.id);
            if (!cur) {
                byId.set(e.id, {
                    id: e.id, channel: e.channel, author: e.author, tsMs: e.tsMs, text: e.text,
                    kind: e.kind, replyTo: e.replyTo, attachmentUrl: e.attachmentUrl, durationS: e.durationS,
                    deleted: false, reactions: {},
                });
            }
        }
        else if (e.op === 'edit') {
            const m = byId.get(e.id);
            if (m && !m.deleted && e.tsMs >= (editedAt.get(e.id) ?? m.tsMs)) {
                m.text = e.text;
                m.editedMs = e.tsMs;
                editedAt.set(e.id, e.tsMs);
            }
        }
        else if (e.op === 'delete') {
            const m = byId.get(e.id);
            // Kun forfatter (eller senere: stab) kan slette — actor er stemplet av hubben.
            if (m && m.author === e.author) {
                m.deleted = true;
                m.text = '';
                m.attachmentUrl = undefined;
            }
        }
        else if (e.op === 'react') {
            const m = byId.get(e.id);
            if (m) {
                const set = new Set(m.reactions[e.emoji] ?? []);
                if (e.add)
                    set.add(e.author);
                else
                    set.delete(e.author);
                if (set.size)
                    m.reactions[e.emoji] = [...set].sort();
                else
                    delete m.reactions[e.emoji];
            }
        }
        else if (e.op === 'typing') {
            typing.set(e.author, { author: e.author, channel: e.channel, tsMs: e.tsMs });
        }
        // 'member'/'topic' er kanal-metadata — se reduceChannels (påvirker ikke meldinger).
    }
    const messages = [...byId.values()].sort((a, b) => a.tsMs - b.tsMs);
    return { messages, typing: [...typing.values()] };
}
/** Reduser strømmen til gjeldende medlemskap + tema per kanal.
 * Medlem: siste 'member'-hendelse per (kanal, subject) vinner (add→med, remove→ute).
 * Tema: nyeste tsMs vinner. Uavhengig av meldingsreduksjonen. */
export function reduceChannels(events) {
    const info = new Map();
    const memberAt = new Map(); // "channel subject" → tsMs (dedup eldre)
    const topicAt = new Map(); // channel → tsMs
    const ensure = (ch) => {
        let c = info.get(ch);
        if (!c) {
            c = { channel: ch, members: [] };
            info.set(ch, c);
        }
        return c;
    };
    for (const e of events) {
        if (e.op === 'member') {
            const key = `${e.channel} ${e.subject}`;
            if (e.tsMs < (memberAt.get(key) ?? -1))
                continue; // eldre enn kjent — hopp over
            memberAt.set(key, e.tsMs);
            const c = ensure(e.channel);
            const rest = c.members.filter((m) => m.id !== e.subject);
            if (e.action === 'add')
                rest.push({ id: e.subject, name: e.name, addedBy: e.author, tsMs: e.tsMs });
            c.members = rest.sort((a, b) => (a.name ?? a.id).localeCompare(b.name ?? b.id, 'nb'));
        }
        else if (e.op === 'topic') {
            if (e.tsMs < (topicAt.get(e.channel) ?? -1))
                continue;
            topicAt.set(e.channel, e.tsMs);
            const c = ensure(e.channel);
            c.topic = e.text;
            c.topicBy = e.author;
            c.topicMs = e.tsMs;
        }
    }
    return info;
}
//# sourceMappingURL=chat.js.map